import axios from 'axios'; // Keep this import to fix ESLint warning
import FileSystemService from '../../services/FileSystemService';
import apiClient from '../../utils/apiConfig';
import { debouncedRequest } from '../../utils/apiHelper';

// Update the ModeActions object to include autoplay-related actions
export const ModeActions = {
    SET_CURRENT_MANIFEST: "SET_CURRENT_MANIFEST",
    SET_DATA_SOURCE: "SET_DATA_SOURCE",
    LOADING_DATA: "LOADING_DATA",
    LOAD_DATA_SUCCESS: "LOAD_DATA_SUCCESS",
    LOAD_DATA_FAILURE: "LOAD_DATA_FAILURE",
    INITIALIZE: "INITIALIZE",
    SET_ADMIN_MODE: "SET_ADMIN_MODE",
    OPEN_ADMIN_PANEL: "OPEN_ADMIN_PANEL",
    CLOSE_ADMIN_PANEL: "CLOSE_ADMIN_PANEL",
    SEARCH_VIDEOS_REQUEST: "SEARCH_VIDEOS_REQUEST",
    SEARCH_VIDEOS_SUCCESS: "SEARCH_VIDEOS_SUCCESS",
    SEARCH_VIDEOS_FAILURE: "SEARCH_VIDEOS_FAILURE",
    UPDATE_SEARCH_FILTERS: "UPDATE_SEARCH_FILTERS",
    CLEAR_SEARCH: "CLEAR_SEARCH",
    SET_SEARCH_VIEW_MODE: "SET_SEARCH_VIEW_MODE",
    UPDATE_SEARCH_HISTORY: "UPDATE_SEARCH_HISTORY",
    // Autoplay action types
    SET_AUTOPLAY_ENABLED: "SET_AUTOPLAY_ENABLED",
    SET_AUTOPLAY_DURATION_TYPE: "SET_AUTOPLAY_DURATION_TYPE",
    SET_AUTOPLAY_PLAYLIST: "SET_AUTOPLAY_PLAYLIST",
    UPDATE_CURRENT_VIDEO_INDEX: "UPDATE_CURRENT_VIDEO_INDEX",
    SET_RANDOM_AUTOPLAY: "SET_RANDOM_AUTOPLAY",
    SET_VIEWING_MODE: "SET_VIEWING_MODE",
    SET_FILMS_AUTOPLAY: "SET_FILMS_AUTOPLAY",
    SET_SHORTS_AUTOPLAY: "SET_SHORTS_AUTOPLAY",
    SET_CLIPS_AUTOPLAY: "SET_CLIPS_AUTOPLAY",
    SET_SAME_TAG_AUTOPLAY_FILMS: "SET_SAME_TAG_AUTOPLAY_FILMS",
    SET_SAME_TAG_AUTOPLAY_SHORTS: "SET_SAME_TAG_AUTOPLAY_SHORTS",
    SET_SAME_TAG_AUTOPLAY_CLIPS: "SET_SAME_TAG_AUTOPLAY_CLIPS",
    // Admin user management action types
    ADMIN_SEARCH_USERS_REQUEST: "ADMIN_SEARCH_USERS_REQUEST",
    ADMIN_SEARCH_USERS_SUCCESS: "ADMIN_SEARCH_USERS_SUCCESS", 
    ADMIN_SEARCH_USERS_FAILURE: "ADMIN_SEARCH_USERS_FAILURE",
    ADMIN_UPDATE_USER_REQUEST: "ADMIN_UPDATE_USER_REQUEST",
    ADMIN_UPDATE_USER_SUCCESS: "ADMIN_UPDATE_USER_SUCCESS",
    ADMIN_UPDATE_USER_FAILURE: "ADMIN_UPDATE_USER_FAILURE",
    ADMIN_SET_USER_VIEW_MODE: "ADMIN_SET_USER_VIEW_MODE",
    ADMIN_SET_SELECTED_USER: "ADMIN_SET_SELECTED_USER",
    ADMIN_CLEAR_SELECTED_USER: "ADMIN_CLEAR_SELECTED_USER",
    ADMIN_UPDATE_USER_FILTERS: "ADMIN_UPDATE_USER_FILTERS",
    // Community user search action types
    COMMUNITY_USER_SEARCH_REQUEST: 'COMMUNITY_USER_SEARCH_REQUEST',
    COMMUNITY_USER_SEARCH_SUCCESS: 'COMMUNITY_USER_SEARCH_SUCCESS',
    COMMUNITY_USER_SEARCH_FAILURE: 'COMMUNITY_USER_SEARCH_FAILURE',
};

export const setCurrentManifest = (manifest) => ({
    type: ModeActions.SET_CURRENT_MANIFEST,
    payload: manifest
});

export const setDataSource = (source) => ({
    type: ModeActions.SET_DATA_SOURCE,
    payload: source
});

export const loadingData = () => ({
    type: ModeActions.LOADING_DATA
});

export const loadDataSuccess = (data) => ({
    type: ModeActions.LOAD_DATA_SUCCESS,
    payload: data
});

export const loadDataFailure = (error) => ({
    type: ModeActions.LOAD_DATA_FAILURE,
    payload: error
});

export const setAdminMode = (isAdminMode) => ({
    type: ModeActions.SET_ADMIN_MODE,
    payload: isAdminMode
});

// Admin panel actions
export const openAdminPanel = () => ({
    type: ModeActions.OPEN_ADMIN_PANEL
});

export const closeAdminPanel = () => ({
    type: ModeActions.CLOSE_ADMIN_PANEL
});

export const findLatestManifest = () => async (dispatch, getState) => {
  const manifestPath = getState().Mode.manifestPath;
  console.log("Finding latest manifest in:", manifestPath);
  
  try {
    // Use debounced request to avoid multiple identical manifest requests
    const cacheKey = 'list-all-manifests';
    const data = await debouncedRequest(cacheKey, async () => {
      // Use the apiClient instance instead of creating a new axios instance
      console.log("Using apiClient for manifest fetch");
      const response = await apiClient.get('/api/manifests');
      return response.data;
    });
    
    console.log("Manifests API response:", data);
    
    if (data.success && data.manifests && data.manifests.length > 0) {
      // Filter for burrow-stream-manifest files (newest first)
      // Skip temp and test files
      const manifests = data.manifests.filter(name => 
        name.startsWith('burrow-stream-manifest') && 
        !name.includes('temp') && 
        !name.includes('test')
      );
      
      console.log("Filtered manifests:", manifests);
      
      if (manifests.length > 0) {
        // Sort the manifests by their date (newest first)
        // The format is burrow-stream-manifest-YYYY-MM-DD-HH-MM-SS.json
        const sortedManifests = [...manifests].sort((a, b) => {
          // Extract date parts from filenames
          const dateA = a.match(/burrow-stream-manifest-(\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2})\.json/);
          const dateB = b.match(/burrow-stream-manifest-(\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2})\.json/);
          
          if (dateA && dateB) {
            return dateB[1].localeCompare(dateA[1]); // Reverse order for newest first
          }
          return 0;
        });
        
        console.log("Sorted manifests:", sortedManifests);
        
        const latestManifest = sortedManifests[0];
        console.log(`Found latest manifest: ${latestManifest}`);
        
        // Update the current manifest in Redux state
        dispatch(setCurrentManifest(latestManifest));
        return latestManifest;
      } else {
        console.warn("No burrow-stream-manifest files found");
        dispatch(setCurrentManifest(null));
        return null;
      }
    } else {
      console.warn("No manifests found in directory");
      dispatch(setCurrentManifest(null));
      return null;
    }
  } catch (error) {
    console.error("Error finding latest manifest:", error);
    dispatch(setCurrentManifest(null));
    return null;
  }
};

// Function to generate temporary edit mode manifest
const activeDataLoads = new Map(); // module-level — join in-flight loads instead of restarting them

// Loads the movie pool for the current manifest path. Callers may fire this
// concurrently (App mount + page-mount hooks); only the FIRST call flips the
// loading flag and hits the network — the rest join the same promise. This
// stops the loading-flag flap that flashed skeletons over ready content
// (Polish Run 1 §4).
export const loadAppropriateData = () => async (dispatch, getState) => {
  const key = `load-data-live-${getState().Mode.manifestPath}`;
  if (activeDataLoads.has(key)) {
    return activeDataLoads.get(key); // already loading this data — join it
  }
  dispatch(loadingData());
  const promise = runDataLoad(dispatch, getState, key);
  activeDataLoads.set(key, promise);
  promise.finally(() => activeDataLoads.delete(key)).catch(() => {});
  return promise;
};

const runDataLoad = async (dispatch, getState, preKey) => {
  try {
    const state = getState().Mode;
    const { manifestPath } = state;
    
    // Create a cache key based on the current manifest
    const cacheKey = `load-data-live-${manifestPath}`;
    
    // Use debounced request to avoid duplicates and leverage caching
    const result = await debouncedRequest(cacheKey, async () => {
      console.log(`Loading data in Live Mode from manifest path: ${manifestPath}`);
      
      try {
        // Get the latest manifest file
        const latestManifest = await dispatch(findLatestManifest());
        
        if (latestManifest) {
          console.log(`Using manifest: ${latestManifest}`);
          
          // Create a manifest-specific cache key
          const manifestCacheKey = `manifest-content-${latestManifest}`;
          
          // Use debounced request for the manifest content
          const manifestData = await debouncedRequest(manifestCacheKey, async () => {
            console.log(`Fetching manifest: ${latestManifest}`);
            const response = await apiClient.get(`/api/manifests/${latestManifest}`);
            return response.data;
          });
          
          if (manifestData && manifestData.manifest && Array.isArray(manifestData.manifest.movies)) {
            const movies = manifestData.manifest.movies;
            console.log(`Loaded ${movies.length} movies from manifest ${latestManifest}`);
            
            // Log first movie to debug
            if (movies.length > 0) {
              console.log('Sample movie data:', movies[0].title);
            }
            
            dispatch(loadDataSuccess({
              source: "cdn-manifest",
              movies: movies
            }));
            
            return movies;
          } else {
            console.error("Invalid manifest data structure");
            dispatch(loadDataFailure("Invalid manifest data structure"));
            return [];
          }
        } else {
          console.error("No manifest found, cannot load Live Mode data");
          dispatch(loadDataFailure("No manifest files found"));
          return [];
        }
      } catch (error) {
        console.error("Error loading manifest in Live Mode:", error);
        dispatch(loadDataFailure(`Failed to load manifest: ${error.message}`));
        return [];
      }
    });
    
    return result;
  } catch (error) {
    console.error("Error loading appropriate data:", error);
    dispatch(loadDataFailure(error.toString()));
    return [];
  }
};

// Add this helper function to ensure axios is used
const checkApiAvailability = async () => {
  try {
    // Use apiClient instead of direct axios call
    const response = await apiClient.get('/api/health');
    return response.data.status === 'ok';
  } catch (error) {
    console.log('API health check failed, but continuing');
    return false;
  }
};

// Fix the initialize function to use ModeActions
export const initialize = () => async (dispatch) => {
  await checkApiAvailability(); // This uses axios directly
  dispatch({ type: ModeActions.INITIALIZE }); // Use ModeActions instead of ActionTypes
  // ... rest of the implementation
};

// Function to initialize admin settings from localStorage
export const initializeAdminSettings = () => (dispatch) => {
    try {
        const settings = JSON.parse(localStorage.getItem('adminSettings')) || {};
        
        console.log('Admin settings loaded from localStorage:', settings);
    } catch (error) {
        console.error('Error loading admin settings from localStorage:', error);
    }
    
    return { success: true };
};

// Search action creators
export const searchVideosRequest = () => ({
    type: ModeActions.SEARCH_VIDEOS_REQUEST
});

export const searchVideosSuccess = (results, totalResults) => ({
    type: ModeActions.SEARCH_VIDEOS_SUCCESS,
    payload: { results, totalResults }
});

export const searchVideosFailure = (error) => ({
    type: ModeActions.SEARCH_VIDEOS_FAILURE,
    payload: error
});

export const updateSearchFilters = (filters) => ({
    type: ModeActions.UPDATE_SEARCH_FILTERS,
    payload: filters
});

export const clearSearch = () => ({
    type: ModeActions.CLEAR_SEARCH
});

export const setSearchViewMode = (mode) => ({
    type: ModeActions.SET_SEARCH_VIEW_MODE,
    payload: mode
});

export const updateSearchHistory = (query) => {
    const recentSearches = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    const newSearches = [
        { query, timestamp: Date.now() },
        ...recentSearches.filter(item => item.query !== query)
    ].slice(0, 10); // Keep only the 10 most recent searches
    
    localStorage.setItem('recentSearches', JSON.stringify(newSearches));
    
    return {
        type: ModeActions.UPDATE_SEARCH_HISTORY,
        payload: newSearches
    };
};

// Thunk action for searching videos
export const searchVideos = (params) => async (dispatch, getState) => {
    dispatch(searchVideosRequest());
    
    console.log('Search request params:', params);
      try {
        console.time('search-execution');
        // Use apiClient with absolute URL instead of relative path
        const response = await apiClient.get('/api/search-video-find', { 
            params: {
                ...params,
            }
        });
        console.timeEnd('search-execution');
        
        // Log successful search
        console.log('Search results count:', response.data.results?.length || 0);
        console.log('Total results:', response.data.totalResults);
        
        // Save search query to history if it exists
        if (params.query) {
            dispatch(updateSearchHistory(params.query));
        }
        
        dispatch(searchVideosSuccess(
            response.data.results || [], 
            response.data.totalResults || 0
        ));
        
        return response.data;
    } catch (error) {        console.timeEnd('search-execution');
        console.error('Search API error:', { 
            endpoint: '/api/search-video-find', 
            status: error.response?.status, 
            message: error.message 
        });
        
        dispatch(searchVideosFailure(
            error.response?.data?.message || error.message || 'Search failed'
        ));
        
        throw error;
    }
};

// Autoplay action creators
export const setAutoplayEnabled = (enabled) => ({
    type: ModeActions.SET_AUTOPLAY_ENABLED,
    payload: enabled
});

export const setAutoplayDurationType = (durationType) => ({
    type: ModeActions.SET_AUTOPLAY_DURATION_TYPE,
    payload: durationType
});

export const setAutoplayPlaylist = (playlist, currentVideoId) => {
    // Find the index of the current video in the playlist
    const currentIndex = playlist.findIndex(video => video.id === currentVideoId);
    
    return {
        type: ModeActions.SET_AUTOPLAY_PLAYLIST,
        payload: {
            playlist,
            currentIndex: currentIndex !== -1 ? currentIndex : 0
        }
    };
};

export const updateCurrentVideoIndex = (index) => ({
    type: ModeActions.UPDATE_CURRENT_VIDEO_INDEX,
    payload: index
});

export const setRandomAutoplay = (enabled) => ({
    type: ModeActions.SET_RANDOM_AUTOPLAY,
    payload: enabled
});

export const setViewingMode = (mode) => ({
    type: ModeActions.SET_VIEWING_MODE,
    payload: mode
});

export const setFilmsAutoplay = (enabled) => ({
    type: ModeActions.SET_FILMS_AUTOPLAY,
    payload: enabled
});

export const setShortsAutoplay = (enabled) => ({
    type: ModeActions.SET_SHORTS_AUTOPLAY,
    payload: enabled
});

export const setClipsAutoplay = (enabled) => ({
    type: ModeActions.SET_CLIPS_AUTOPLAY,
    payload: enabled
});

export const setSameTagAutoplayFilms = (enabled) => ({
    type: ModeActions.SET_SAME_TAG_AUTOPLAY_FILMS,
    payload: enabled
});

export const setSameTagAutoplayShorts = (enabled) => ({
    type: ModeActions.SET_SAME_TAG_AUTOPLAY_SHORTS,
    payload: enabled
});

export const setSameTagAutoplayClips = (enabled) => ({
    type: ModeActions.SET_SAME_TAG_AUTOPLAY_CLIPS,
    payload: enabled
});

// Updated helper function to always find a random next video for autoplay
// to prevent looping through the same videos
export const findNextVideo = (durationType = 'all') => (dispatch, getState) => {
    const state = getState().Mode;
    const { autoplay } = state;
    const { currentPlaylist, currentVideoIndex } = autoplay;
    
    if (!currentPlaylist || currentPlaylist.length === 0) {
        console.warn("No playlist available for autoplay");
        return null;
    }
    
    // Duration filtering
    const filteredPlaylist = currentPlaylist.filter(video => {
        const duration = video.duration || video.content?.duration || 0;
        const minutes = duration / 60;
        
        switch(durationType) {
            case 'films':
                return minutes >= 15;
            case 'shorts':
                return minutes >= 3 && minutes < 15;
            case 'clips':
                return minutes < 3;
            default:
                return true;
        }
    });

    // Track recently played videos to avoid immediate repetition
    const recentlyPlayed = JSON.parse(localStorage.getItem('recentlyPlayedVideos') || '[]');
    
    // Get all available indices excluding the current one and recently played
    const availableIndices = [...Array(filteredPlaylist.length).keys()]
        .filter(i => i !== currentVideoIndex && 
            !recentlyPlayed.includes(filteredPlaylist[i]?.id));
    
    if (availableIndices.length === 0) {
        // If all videos have been recently played, use all videos except current
        const fallbackIndices = [...Array(filteredPlaylist.length).keys()]
            .filter(i => i !== currentVideoIndex);
            
        if (fallbackIndices.length === 0) {
            return null;
        }
        
        // Clear the recently played list since we're starting over
        localStorage.setItem('recentlyPlayedVideos', JSON.stringify([]));
        
        // Select a random index from fallback indices
        const randomIdx = Math.floor(Math.random() * fallbackIndices.length);
        const nextIndex = fallbackIndices[randomIdx];
        
        // Update the current video index in the store
        dispatch(updateCurrentVideoIndex(nextIndex));
        
        // Update recently played videos
        const nextVideoId = filteredPlaylist[nextIndex]?.id;
        if (nextVideoId) {
            localStorage.setItem('recentlyPlayedVideos', JSON.stringify([nextVideoId]));
        }
        
        return filteredPlaylist[nextIndex];
    }
    
    // Pick a random index from available indices
    const randomIdx = Math.floor(Math.random() * availableIndices.length);
    const nextIndex = availableIndices[randomIdx];
    
    // Update the current video index in the store
    dispatch(updateCurrentVideoIndex(nextIndex));
    
    // Update recently played videos (keep last 5)
    const nextVideoId = filteredPlaylist[nextIndex]?.id;
    if (nextVideoId) {
        const updatedRecentlyPlayed = [...recentlyPlayed, nextVideoId].slice(-5);
        localStorage.setItem('recentlyPlayedVideos', JSON.stringify(updatedRecentlyPlayed));
    }
    
    return filteredPlaylist[nextIndex];
};

// Function to generate a playlist based on the current video's primary tag
export const generatePlaylistByTag = (videoId, primaryTag = null) => (dispatch, getState) => {
    const state = getState().Mode;
    const { dataSource, autoplay } = state;
    const { movies } = dataSource;
    const { durationType } = autoplay;
    
    if (!movies || movies.length === 0) {
        console.log("No movies data available to generate playlist");
        return [];
    }
    
    // Find current video
    const currentVideo = movies.find(video => video.id === videoId);
    if (!currentVideo) {
        console.warn(`Video with ID ${videoId} not found in the data source`);
        return [];
    }
    
    // If primaryTag not provided, get the first tag from the current video
    let tagToUse = primaryTag;
    if (!tagToUse && currentVideo.tags && currentVideo.tags.length > 0) {
        tagToUse = typeof currentVideo.tags[0] === 'string' 
            ? currentVideo.tags[0] 
            : (currentVideo.tags[0].name || '');
    }
    
    console.log(`Generating playlist with tag: ${tagToUse}, duration type: ${durationType}`);
    
    // If still no tag, use all videos
    let filteredVideos;
    if (!tagToUse) {
        filteredVideos = [...movies].filter(video => video && video.id !== videoId);
    } else {
        // Filter videos with the same tag
        filteredVideos = movies.filter(video => 
            video && 
            video.id !== videoId && // Don't include the current video
            video.tags && 
            Array.isArray(video.tags) &&
            video.tags.some(tag => {
                const tagValue = typeof tag === 'string' ? tag : (tag.name || '');
                return tagValue.toLowerCase().includes(tagToUse.toLowerCase());
            })
        );
    }
    
    // Enhanced logging to debug duration filtering
    console.log(`Pre-duration filter: ${filteredVideos.length} videos`);

    // Apply duration filters if needed
    if (durationType !== 'all') {
        filteredVideos = filteredVideos.filter(video => {
            // Get duration from all possible sources
            const durationRaw = video.duration || 
                               video.length || 
                               (video.content && video.content.duration) ||
                               0;
            
            // Convert duration to minutes for filtering
            const durationInMinutes = getDurationInMinutes(durationRaw);
            
            // Log duration for debugging
            console.log(`Video ${video.id} (${video.title}) - Duration: ${durationRaw} (${durationInMinutes} minutes)`);
            
            switch(durationType) {
                case 'clips':
                    return durationInMinutes < 3;
                case 'shorts':
                    return durationInMinutes >= 3 && durationInMinutes <= 15;
                case 'films':
                    return durationInMinutes > 15;
                default:
                    return true;
            }
        });
    }
    
    console.log(`Generated filtered playlist with ${filteredVideos.length} videos for duration type: ${durationType}`);
    
    // Clear recently played videos when generating a new playlist
    localStorage.setItem('recentlyPlayedVideos', JSON.stringify([]));
    
    // Dispatch the playlist and current video index
    dispatch(setAutoplayPlaylist(filteredVideos, videoId));
    
    return filteredVideos;
};

// Improved helper function to convert duration string to minutes with enhanced format support
const getDurationInMinutes = (durationString) => {
    // Check for null or undefined
    if (durationString === null || durationString === undefined) {
        console.log('Duration is null or undefined');
        return 0;
    }
    
    // Handle numeric values (assumed to be seconds)
    if (typeof durationString === 'number') {
        const minutes = durationString / 60; 
        console.log(`Numeric duration: ${durationString}s = ${minutes} minutes`);
        return minutes;
    }
    
    // Handle string formats
    if (typeof durationString === 'string') {
        // Format HH:MM:SS or MM:SS
        if (durationString.includes(':')) {
            const parts = durationString.split(':');
            
            if (parts.length === 3) {
                // HH:MM:SS format
                const hours = parseInt(parts[0], 10) || 0;
                const minutes = parseInt(parts[1], 10) || 0;
                const seconds = parseInt(parts[2], 10) || 0;
                const totalMinutes = hours * 60 + minutes + seconds / 60;
                console.log(`HH:MM:SS format: ${durationString} = ${totalMinutes} minutes`);
                return totalMinutes;
            }
            
            if (parts.length === 2) {
                // MM:SS format
                const minutes = parseInt(parts[0], 10) || 0;
                const seconds = parseInt(parts[1], 10) || 0;
                const totalMinutes = minutes + seconds / 60;
                console.log(`MM:SS format: ${durationString} = ${totalMinutes} minutes`);
                return totalMinutes;
            }
        }
        
        // Try to parse as direct number (e.g., "120" seconds)
        const parsed = parseFloat(durationString);
        if (!isNaN(parsed)) {
            const minutes = parsed / 60;
            console.log(`String numeric duration: ${durationString}s = ${minutes} minutes`);
            return minutes;
        }
    }
    
    console.log(`Could not parse duration: ${durationString}, returning 0`);
    return 0;
};

// User search action creators
export const adminSearchUsersRequest = () => ({
    type: ModeActions.ADMIN_SEARCH_USERS_REQUEST
});

export const adminSearchUsersSuccess = (results, totalResults) => ({
    type: ModeActions.ADMIN_SEARCH_USERS_SUCCESS,
    payload: { results, totalResults }
});

export const adminSearchUsersFailure = (error) => ({
    type: ModeActions.ADMIN_SEARCH_USERS_FAILURE,
    payload: error
});

export const adminSetUserViewMode = (mode) => ({
    type: ModeActions.ADMIN_SET_USER_VIEW_MODE,
    payload: mode
});

export const adminSetSelectedUser = (user) => ({
    type: ModeActions.ADMIN_SET_SELECTED_USER,
    payload: user
});

export const adminClearSelectedUser = () => ({
    type: ModeActions.ADMIN_CLEAR_SELECTED_USER
});

export const adminUpdateUserFilters = (filters) => ({
    type: ModeActions.ADMIN_UPDATE_USER_FILTERS,
    payload: filters
});

// Thunk action for searching users
export const adminSearchUsers = (params) => async (dispatch) => {
    dispatch(adminSearchUsersRequest());
    
    console.log('Admin User Search request params:', params);
    console.time('admin-user-search');
    
    try {
        const response = await apiClient.get('/api/admin-user-search', { 
            params: params
        });
        console.timeEnd('admin-user-search');
        
        console.log('Admin User Search results count:', response.data.results?.length || 0);
        console.log('Total users found:', response.data.totalResults);
        
        dispatch(adminSearchUsersSuccess(
            response.data.results || [], 
            response.data.totalResults || 0
        ));
        
        return response.data;
    } catch (error) {
        console.timeEnd('admin-user-search');
        console.error('Admin User Search API error:', error);
        
        dispatch(adminSearchUsersFailure(
            error.response?.data?.message || error.message || 'Error searching users'
        ));
        
        return { success: false, error: error.message };
    }
};

// Thunk for updating user
export const adminUpdateUser = (userId, updates) => async (dispatch) => {
    dispatch({ type: ModeActions.ADMIN_UPDATE_USER_REQUEST });
    
    console.log(`Updating user ${userId}:`, updates);
    
    try {
        const response = await apiClient.put(`/api/admin/user/${userId}`, updates);
        
        console.log('User update response:', response.data);
        
        dispatch({ 
            type: ModeActions.ADMIN_UPDATE_USER_SUCCESS,
            payload: response.data.user
        });
        
        return { success: true, user: response.data.user };
    } catch (error) {
        console.error('Error updating user:', error);
        
        dispatch({ 
            type: ModeActions.ADMIN_UPDATE_USER_FAILURE,
            payload: error.response?.data?.message || error.message || 'Error updating user'
        });
        
        return { success: false, error: error.message };
    }
};

// Community user search action creators
export const communityUserSearchRequest = () => ({
  type: ModeActions.COMMUNITY_USER_SEARCH_REQUEST
});

export const communityUserSearchSuccess = (data) => ({
  type: ModeActions.COMMUNITY_USER_SEARCH_SUCCESS,
  payload: data
});

export const communityUserSearchFailure = (error) => ({
  type: ModeActions.COMMUNITY_USER_SEARCH_FAILURE,
  payload: error
});

/**
 * Search for users in the community
 * Uses apiClient to ensure requests go to the backend API server
 */
export const searchCommunityUsers = (params = {}) => {
  return async (dispatch, getState) => {
    try {
      dispatch(communityUserSearchRequest());
      
      // Use user-search-user endpoint which is accessible to regular users
      const response = await apiClient.get('/api/user-search-user', {
        params: params
      });
      
      const data = response.data;
      
      dispatch(communityUserSearchSuccess({
        results: data.users || [],
        totalResults: data.totalResults || 0,
        currentPage: parseInt(data.currentPage) || 1,
        totalPages: parseInt(data.totalPages) || 1,
        hasMore: data.hasMore
      }));
      
      return data;
    } catch (error) {
      console.error('Community search error:', error);
      
      // Log additional error details if available
      if (error.response) {
        console.error(`API Error: ${error.response.status} - ${error.response.statusText}`);
        console.error('Error details:', error.response.data);
      }
      
      dispatch(communityUserSearchFailure(
        error.response?.data?.message || error.message || 'Error searching users'
      ));
      return null;
    }
  };
};

/**
 * Update community user search filters
 */
export const updateCommunityUserFilters = (filters) => ({
  type: 'UPDATE_COMMUNITY_USER_FILTERS',
  payload: filters
});

/**
 * Set community user view mode
 */
export const setCommunityUserViewMode = (mode) => ({
  type: 'SET_COMMUNITY_USER_VIEW_MODE',
  payload: mode
});
